import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';

/** Lower-cases and strips accents, so that "ecole" matches "École". */
export function normalizeForSearch(value: string): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/** Items whose label contains the term (accent and case insensitive), at most maxItems of them. */
export function filterByLabel<T>(items: T[], term: string, label: (item: T) => string, maxItems: number): T[] {
  const needle = normalizeForSearch(term);
  const source = items || [];
  const matches = needle ? source.filter((item) => normalizeForSearch(label(item)).includes(needle)) : source;
  return matches.slice(0, maxItems);
}

/** Id sent when typed text matches no option: the backend then returns nothing. */
export const NO_MATCH_ID = -1;

/**
 * For filters whose backend only accepts ids: the ids of every option whose label
 * contains the typed text, so that typing without picking still filters.
 * Null when the text is empty (no filter), [NO_MATCH_ID] when nothing matches.
 */
export function idsMatchingText<T>(items: T[], text: string, label: (item: T) => string, idOf: (item: T) => number): number[] {
  if (!normalizeForSearch(text)) {
    return null;
  }
  const ids = filterByLabel(items, text, label, Number.MAX_SAFE_INTEGER).map(idOf);
  return ids.length ? ids : [NO_MATCH_ID];
}

/**
 * Text input with a filtered suggestion list ("text input + filtered list").
 *
 * - Static mode: `items` is filtered in the browser.
 * - Async mode: `search(term)` is called (debounced) once the term reaches `minLength`.
 *
 * Typing emits `textChange` (the page keeps filtering on the free text); picking a
 * suggestion emits `selected` only, so the page can filter precisely on the item.
 */
@Component({
  standalone: true,
  selector: 'app-autocomplete',
  templateUrl: 'autocomplete.component.html',
  // No ngModel on the inner input: its ng-valid / ng-invalid classes would override inputClass
  imports: [CommonModule],
  styles: [
    `
      .ac {
        display: inline-block;
        position: relative;
        margin-right: 10px;
        margin-bottom: 5px;
      }
      .ac input {
        width: 100%;
      }
      .ac.ac-block {
        display: block;
        margin: 0;
      }
      .ac-list {
        position: absolute;
        top: 100%;
        left: 0;
        width: max-content;
        min-width: 100%;
        max-width: 420px;
        margin: 0;
        padding: 0;
        list-style: none;
        background: white;
        border: 1px solid #ccc;
        border-top: none;
        max-height: 200px;
        overflow-y: auto;
        overflow-x: hidden;
        z-index: 1000;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      }
      .ac-list li {
        padding: 8px 12px;
        cursor: pointer;
        border-bottom: 1px solid #eee;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .ac-list li .ac-detail {
        margin-left: 6px;
        color: #999;
      }
      .ac-list li.active,
      .ac-list li:hover {
        background-color: #f5f5f5;
      }
    `,
  ],
})
export class AutocompleteComponent<T = any> implements OnChanges, OnDestroy {
  @Input() placeholder = '';
  /** Static mode: the full list, filtered in the browser */
  @Input() items: T[] = null;
  /** Async mode: server-side search */
  @Input() search: (term: string) => Observable<T[]> = null;
  @Input() label: (item: T) => string = (item: any) => (item == null ? '' : String(item));
  /** Optional secondary text shown in grey in the list (e.g. a city), to tell homonyms apart */
  @Input() detail: (item: T) => string = null;
  /** Minimum term length before suggesting; defaults to 0 (static) or 2 (async) */
  @Input() minLength: number = null;
  @Input() maxItems = 20;
  @Input() width = '210px';
  /** Form usage: full width, no margins, and e.g. inputClass="form-control" */
  @Input() block = false;
  @Input() inputClass = '';
  /** Adds the required attribute, for the app's valid/invalid input styling */
  @Input() required = false;
  @Input() text = '';
  @Output() textChange = new EventEmitter<string>();
  @Output() selected = new EventEmitter<T>();

  suggestions: T[] = [];
  open = false;
  activeIndex = -1;

  private terms = new Subject<string>();
  private subscription: Subscription;

  constructor() {
    this.subscription = this.terms
      .pipe(
        debounceTime(250),
        switchMap((term) => (this.search ? this.search(term).pipe(catchError(() => of([] as T[]))) : of([] as T[]))),
      )
      .subscribe((items) => this.setSuggestions(items));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && this.open) {
      this.refresh(this.text);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onInput(value: string): void {
    this.text = value;
    this.textChange.emit(value);
    this.open = true;
    this.refresh(value);
  }

  onFocus(): void {
    this.open = true;
    this.refresh(this.text);
  }

  onBlur(): void {
    this.open = false;
  }

  select(item: T): void {
    this.text = this.label(item);
    this.open = false;
    this.activeIndex = -1;
    this.selected.emit(item);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.suggestions.length) {
        return;
      }
      event.preventDefault();
      this.open = true;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const count = this.suggestions.length;
      this.activeIndex = (this.activeIndex + step + count) % count;
    } else if (event.key === 'Enter') {
      if (this.open && this.activeIndex >= 0 && this.activeIndex < this.suggestions.length) {
        event.preventDefault();
        this.select(this.suggestions[this.activeIndex]);
      }
    } else if (event.key === 'Escape') {
      this.open = false;
      this.activeIndex = -1;
    }
  }

  itemTitle(item: T): string {
    const detail = this.detail ? this.detail(item) : null;
    return detail ? `${this.label(item)} (${detail})` : this.label(item);
  }

  private get effectiveMinLength(): number {
    if (this.minLength !== null && this.minLength !== undefined) {
      return this.minLength;
    }
    return this.search ? 2 : 0;
  }

  private refresh(term: string): void {
    const trimmed = (term || '').trim();
    if (trimmed.length < this.effectiveMinLength) {
      this.setSuggestions([]);
      return;
    }
    if (this.search) {
      this.terms.next(trimmed);
    } else {
      this.setSuggestions(filterByLabel(this.items, trimmed, this.label, this.maxItems));
    }
  }

  private setSuggestions(items: T[]): void {
    this.suggestions = (items || []).slice(0, this.maxItems);
    this.activeIndex = -1;
  }
}
